import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { ErrorStateMatcher, MatOptionSelectionChange } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';

import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MtnaPopoverConfig } from '@mtna/lib-ui';
import { Variable, VariableSummary } from '@mtna/pojo-consumer-ui';

import { RdsChip } from '../chips/index';
import { RdsVariableUtil } from '../core/utils/variable-util';

/** Error when invalid control is dirty, touched, or submitted. */
export class RdsVariableSelectErrorStateMatcher implements ErrorStateMatcher {
  constructor(private _control: NgControl) {}
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(this._control && this._control.invalid && (this._control.dirty || this._control.touched || isSubmitted));
  }
}

/**
 * Select control to select variables, uses a virtual scroll, search, and supports reactive forms.
 * @author Will Davis <will.davis@mtna.us>
 */
@Component({
  selector: 'rds-variable-select',
  templateUrl: './variable-select.component.html',
  styleUrls: ['./variable-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RdsVariableSelectComponent<T extends Variable | VariableSummary> implements ControlValueAccessor, DoCheck, OnDestroy, OnInit {
  /** Popover configuration to describe the control */
  @Input() config: MtnaPopoverConfig = new MtnaPopoverConfig('Variables', 'Select a few variables!', 'after');

  /** Whether the control is disabled */
  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(value: any) {
    const newValue = coerceBooleanProperty(value);
    if (newValue !== this.disabled) {
      this._disabled = newValue;
      this._changeDetectorRef.markForCheck();
    }
  }
  private _disabled = false;

  /** Disabled select options */
  @Input()
  set disabledOptions(options: Array<T>) {
    this._disabledOptions = options;
    this._changeDetectorRef.markForCheck();
  }
  get disabledOptions() {
    return this._disabledOptions;
  }
  private _disabledOptions: Array<T>;

  /** Maximum number of selections */
  @Input() maxSelection: number | null = null;
  /** Whether the user can make multiple selections. Default true */
  @Input() multiSelect = true;

  /** All the variables to choose from in the select */
  @Input()
  set options(options: Array<T>) {
    this._options = options;
    this._filteredVariables$.next(!this.options ? [] : this.options.slice());
    this._changeDetectorRef.markForCheck();
  }
  get options() {
    return this._options;
  }
  private _options: Array<T>;
  /** Whether the variables can be reorderd in the chip list */
  @Input() orderable = false;
  /** Whether the control is required */
  @Input() required = false;
  /** Label for mat-select, defaults to "Variable(s)" */
  @Input() selectLabel = 'Variable(s)';
  /** Placeholder for mat-select, defaults to "Choose variables..." */
  @Input() selectPlaceholder = 'Choose variables...';
  /** Suffix icon for the select, defaults to null */
  @Input() suffixIcon: string | null = null;

  /** The selected variables and value for this control, if it exists */
  @Input()
  set value(value: Array<T>) {
    if (value !== this.value) {
      this._value = value;
      this._changeDetectorRef.markForCheck();
    }
  }
  get value() {
    return this._value;
  }
  private _value: Array<T> | null;

  /** Changed value of the form control */
  @Output() readonly selectionChange = new EventEmitter<T[]>();

  /** Internal <mat-select> */
  @ViewChild(MatSelect) matSelect: MatSelect;

  /** Custom error state matcher */
  _matcher: RdsVariableSelectErrorStateMatcher;
  /** Whether any search results were found */
  _noSearchResults = false;
  /** A subset of all the variables, filtered by the search text */
  _filteredVariables$ = new ReplaySubject<T[]>(1);

  private _onDestroy = new Subject<void>();

  _onTouched: () => any = () => {};
  private _controlValueAccessorChangeFn: (value: any) => void = () => {};

  constructor(
    @Self() @Optional() public _control: NgControl,
    elementRef: ElementRef<HTMLElement>,
    private _changeDetectorRef: ChangeDetectorRef,
    private _focusMonitor: FocusMonitor
  ) {
    if (this._control) {
      this._control.valueAccessor = this;
      this._matcher = new RdsVariableSelectErrorStateMatcher(this._control);
    }
    this._focusMonitor.monitor(elementRef, true).subscribe((focusOrigin) => {
      if (!focusOrigin) {
        // When a focused element becomes disabled, the browser *immediately* fires a blur event.
        // Angular does not expect events to be raised during change detection, so any state change
        // (such as a form control's 'ng-touched') will cause a changed-after-checked error.
        // See https://github.com/angular/angular/issues/17793. To work around this, we defer
        // telling the form control it has been touched until the next tick.
        Promise.resolve().then(() => this._onTouched());
      }
    });
  }

  /**
   * When the mtna-select-search emits search text, filter the visible variables in the select.
   *
   * @param text search text to filter the variables
   */
  filterVariables(text: string) {
    if (!this.options || !this.matSelect || !this.matSelect.panelOpen) {
      return;
    }
    if (!text) {
      this._filteredVariables$.next(this.options.slice());
      return;
    }
    // filter the variable summaries if search text exists in name or label
    this._filteredVariables$.next(this.options.filter((v) => RdsVariableUtil.filterVariable(text, v)));
  }

  /**
   * When the select panel is opened or closed, manipulate the options visible.
   * When the panel is opened, show the full list of variables.
   * When the panel is closed, set the selected value on the filtered subject so it will work with the MatSelect's internal selection model.
   *
   * @param opened Whether the panel is being opened
   */
  handleOpenedChange(opened: boolean) {
    if (opened) {
      // When the panel is opened, reset the filter to show all the variables
      this.filterVariables('');
    } else if (!!this.value && !!this.value.length) {
      // When the panel is closed and there is a selected value,
      // set only that value as an option so the internal MatSelect's selection model will know that option is selected
      this._filteredVariables$.next(this.value);
    }
  }

  ngDoCheck(): void {
    if (!!this.matSelect) {
      this.matSelect.updateErrorState();
    }
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  ngOnInit() {
    // Determine `_noSearchResults` each time the filtered variables changes
    this._filteredVariables$.pipe(takeUntil(this._onDestroy)).subscribe((filteredVariables) => {
      const newValue = filteredVariables.length === 0;
      if (newValue !== this._noSearchResults) {
        this._noSearchResults = newValue;
        this._changeDetectorRef.markForCheck();
      }
    });
  }

  registerOnChange(fn: any): void {
    this._controlValueAccessorChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(obj: any): void {
    this.value = obj;
  }

  /**
   * Comparison function for use with `compareWith`
   */
  _compareVariable(v1: T, v2: T) {
    if (!v1 || !v2) {
      return false;
    }
    return v1.uri === v2.uri || v1.id === v2.id;
  }

  _emitChangeEvent(newValue: Array<T>) {
    this.value = newValue;
    this._controlValueAccessorChangeFn(this.value);
    this.selectionChange.emit(this.value);
  }

  /**
   * Manually handle when options are selected/unselected.
   * @param event option selection event
   */
  _handleOptionSelectionChange(event: MatOptionSelectionChange) {
    // Only proceed if it is from user input
    if (event.isUserInput) {
      if (this.multiSelect) {
        this._handleMultiOptionSelectionChange(event);
      } else {
        this._handleSingleOptionSelectionChange(event);
      }
    }
  }

  /**
   * Removes a variable from the array of selected variables;
   * @param id the variable identifier
   */
  _removeVariable(id: string) {
    this._emitChangeEvent(this.value.filter((v) => (v.uri || v.id) !== id));
  }

  _reorderVariables(event: Array<RdsChip>) {
    this._emitChangeEvent(event.map((chip) => this.value.find((v) => v.uri === chip.id || v.id === chip.id)));
  }

  _trackByVariable(_: number, variable: Variable) {
    if (!variable) {
      return null;
    }
    return variable.uri || variable.id;
  }

  /**
   * Manually handle when options are selected/unselected in a multi select
   * @param event option selection event
   */
  private _handleMultiOptionSelectionChange(event: MatOptionSelectionChange) {
    const variable: T = event.source.value;
    if (event.source.selected) {
      // If the option was selecte, add it to the existing options
      const oldValues = !!this.value ? this.value : [];
      this._emitChangeEvent([...oldValues, variable]);
    } else if (!event.source.selected) {
      this._removeVariable(variable.uri || variable.id);
    }
  }

  /**
   * Manually handle when options are selected/unselected in a single select
   * @param event option selection event
   */
  private _handleSingleOptionSelectionChange(event: MatOptionSelectionChange) {
    const variable: T = event.source.value;
    if (event.source.selected && (!this.value || !this.value.some((v) => this._compareVariable(v, variable)))) {
      // If the option was selected and not already selected, set it
      this._emitChangeEvent([variable]);
    } else if (!event.source.selected) {
      this._removeVariable(variable.uri || variable.id);
    }
  }
}
